import { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { setPendingResume } from "@/lib/pendingResume";

const STATS = [
  { value: "10×", label: "Faster screening" },
  { value: "98%", label: "Time saved on CVs" },
  { value: "Multi-org", label: "Client management" },
];

export function LandingHero() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      setPendingResume(files[0]);
      navigate("/signup");
    },
    [navigate],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      handleFiles(Array.from(e.dataTransfer.files || []));
    },
    [handleFiles],
  );

  return (
    <section
      style={{
        background: "linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 100%)",
      }}
      className="relative overflow-hidden pt-16"
    >
      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Glow accent */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] opacity-20 blur-[120px]"
        style={{
          background: "radial-gradient(ellipse, #ff385c 0%, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — copy + CTAs + stats */}
          <div>
            {/* Badge */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-rausch animate-pulse" />
              <span className="text-xs font-semibold text-white/70 uppercase tracking-widest">
                AI-Powered ATS Platform
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              Screen Every CV.
              <br />
              <span className="text-rausch">Hire</span> The Best.
            </h1>

            {/* Subheadline */}
            <p className="mt-6 text-lg sm:text-xl text-white/60 max-w-xl leading-relaxed">
              The recruiting platform built for HR outsourcing firms and
              enterprise talent teams. Process hundreds of resumes against
              custom hiring matrices — and deliver professional results to every
              client.
            </p>

            {/* Primary CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-btn bg-rausch px-8 py-3.5 text-base font-semibold text-white hover:bg-[#e0334f] transition-colors shadow-lg"
              >
                Start Screening Free
              </Link>
              <a
                href="#pricing"
                className="inline-flex items-center justify-center rounded-btn border border-white/20 bg-white/5 px-8 py-3.5 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                View Pricing
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 flex flex-wrap gap-8 border-t border-white/10 pt-10">
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="mt-1 text-sm text-white/50">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — drop zone */}
          <div className="flex flex-col lg:-mt-24">
            <p className="mb-4 text-xs font-semibold text-white/40 uppercase tracking-widest text-center">
              Try it now — drop a resume
            </p>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={[
                "flex flex-col items-center justify-center rounded-card border-2 border-dashed p-12 text-center cursor-pointer transition-all min-h-[320px]",
                dragging
                  ? "border-rausch bg-rausch/10"
                  : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
              ].join(" ")}
            >
              <svg
                className={`mb-4 h-14 w-14 transition-colors ${dragging ? "text-rausch" : "text-white/30"}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
                />
              </svg>
              <p className="text-lg font-semibold text-white">
                {dragging
                  ? "Drop to start screening"
                  : "Drag & drop a resume here"}
              </p>
              <p className="mt-2 text-sm text-white/50">
                or{" "}
                <span className="text-rausch font-medium underline">
                  browse
                </span>{" "}
                — PDF or TXT
              </p>
              <p className="mt-3 text-xs text-white/30 max-w-xs">
                We'll guide you through setup after sign-up
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.txt,.md,application/pdf,text/plain"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) handleFiles(files);
                  e.target.value = "";
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
